import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";
import { PaymentApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { PageHeader, Spinner, StatusBadge } from "../../components/common";
import { peso } from "../../lib/format";

const METHODS = ["QR Ph", "GCash", "Maya", "Card"];

export default function PaySimulated() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [method, setMethod] = useState(METHODS[0]);
  const [busy, setBusy] = useState(false);
  const { data: p, isLoading, refetch } = useQuery({ queryKey: ["payment", id], queryFn: () => PaymentApi.get(id) });

  const run = async (outcome: "paid" | "failed") => {
    setBusy(true);
    try { await PaymentApi.simulate(id, outcome); await refetch(); qc.invalidateQueries(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  if (isLoading || !p) return <Spinner />;
  const back = p.purpose === "membership" ? "/patient/membership" : `/patient/bookings/${p.ref_id}`;
  return (
    <div className="space-y-5">
      <PageHeader title="Test checkout" />
      <div className="flex gap-3 rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 p-4 text-amber-900" data-testid="simulated-payment-banner">
        <FlaskConical className="shrink-0" />
        <p className="text-sm"><strong>SIMULATED TEST PAYMENT.</strong> PayMongo is not connected yet. No money moves and no real QR Ph, GCash, Maya or card charge happens.</p>
      </div>
      <div className="fp-card space-y-4">
        <div className="flex items-start justify-between">
          <div><p className="fp-label">Paying for</p><p className="font-semibold">{p.description}</p></div>
          <StatusBadge status={p.status} testId="simulated-payment-status" />
        </div>
        <p className="text-3xl font-extrabold text-[#036248]" data-testid="simulated-payment-amount">{peso(p.amount)}</p>
        {p.status === "pending" ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              {METHODS.map((m) => (
                <button key={m} onClick={() => setMethod(m)} data-testid={`sim-method-${m.toLowerCase().replace(/\s/g, "")}`}
                  className={`min-h-[48px] rounded-xl border text-sm font-semibold ${method === m ? "border-[#036248] bg-emerald-50 text-[#036248]" : "border-slate-300"}`}>{m}</button>
              ))}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button className="fp-btn-primary flex-1" disabled={busy} onClick={() => run("paid")} data-testid="simulate-payment-success">Simulate successful {method} payment</button>
              <button className="fp-btn-secondary flex-1" disabled={busy} onClick={() => run("failed")} data-testid="simulate-payment-failure">Simulate failure</button>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-700" data-testid="simulated-payment-result">Server recorded this test payment as <strong>{p.status}</strong>.</p>
        )}
      </div>
      <Link to={back} className="fp-btn-secondary w-full" data-testid="simulated-payment-back">Back</Link>
    </div>
  );
}
